import { Constants } from '../../internal/Constants';
import { FormatPhoneE164 } from '../../internal/Helpers';
import { Localized } from '../../internal/Localized';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { useTheme } from '../../providers/ThemeProvider';
import Styles from './About.module.css';
import { Audio, Flipbook, Label, Video } from '@app/engine';
import { LobbyClient } from '@shared/frontend';
import { useMemo } from 'react';

/** File-local constants for DemoTab. */
const localConstants = {
  // Telemetry surface key attached to demo media play events (video and audio share the one surface; the media_type param distinguishes them).
  telemetrySurfaceDemo: 'about_demo'
} as const;

/**
 * Demo tab — surfaces the operator-configured demo media from global settings as up to three stacked sections: Watch (video), Listen (audio), and Live (call number). Each section renders only
 * when its source exists; when none are configured a single "currently unavailable" panel is shown instead.
 *
 * @returns The demo tab element.
 */
export function DemoTab() {
  const { getThemedAssetDTO } = useTheme();
  const { isRTL } = useLocale();
  const { demoE164, audioKey, videoKey } = useLobbySettings();

  // The demo media bytes stream from the public lobby endpoints; the storage key doubles as a cache-busting version token so a replaced file is re-fetched.
  const videoUrl = useMemo(() => (videoKey ? LobbyClient.GetLobbyVideoUrl(videoKey) : ''), [videoKey]);
  const audioUrl = useMemo(() => (audioKey ? LobbyClient.GetLobbyAudioUrl(audioKey) : ''), [audioKey]);
  const flipbookAssetDTOs = useMemo(() => [getThemedAssetDTO(Constants.Bundle.Preload, 'Head.png'), getThemedAssetDTO(Constants.Bundle.Preload, 'HeadCall.png')], [getThemedAssetDTO]);
  const callFormatted = useMemo(() => (demoE164 ? `${Localized.LandingDemoCallPrefix}${FormatPhoneE164(demoE164)}` : ''), [demoE164]);
  const textFormatted = useMemo(() => (demoE164 ? `${Localized.LandingDemoTextPrefix}${FormatPhoneE164(demoE164)}` : ''), [demoE164]);

  // When nothing is configured, show the single fallback panel rather than an empty tab.
  if (!videoKey && !audioKey && !demoE164) {
    return (
      <section>
        <Label text={Localized.AboutDemoLabel} classNameContainer={Styles.sectionLabel} />
        <Label text={Localized.AboutDemoTitle} classNameContainer={Styles.sectionTitle} />
        <div className={Styles.bubblePanel}>
          <Label text={Localized.AboutDemoUnavailable} classNameContainer={Styles.bubblePanelText} />
        </div>
      </section>
    );
  }

  return (
    <section>
      {demoE164 && (
        <div className={Styles.demoSection}>
          <Label text={Localized.AboutDemoLiveLabel} classNameContainer={Styles.sectionLabel} />
          <Label text={Localized.AboutDemoLiveTitle} classNameContainer={Styles.sectionTitle} />
          <div className={`${Styles.bubblePanel} ${Styles.livePanel}`}>
            <Flipbook
              title={Localized.AboutDemoLivePhotoAlt}
              assetDTOs={flipbookAssetDTOs}
              rtl={isRTL}
              textLoading={Localized.DashLoading}
              classNameContainer={Styles.liveFlipbookWrap}
              classNameImage={Styles.liveFlipbookImage}
            />
            <div className={Styles.livePhoneWrap}>
              {/* The call/text pills sit side-by-side in landscape (mirroring the landing demo card) and stack in portrait (see the portrait rule in About.module.css). */}
              <div className={Styles.livePillRow}>
                <a href={`tel:${demoE164}`} className={Styles.livePhone}>
                  {callFormatted}
                </a>
                <a href={`sms:${demoE164}`} className={Styles.liveText}>
                  {textFormatted}
                </a>
              </div>
              <Label text={Localized.AboutDemoLiveSubtitle} classNameContainer={Styles.bubblePanelText} />
            </div>
          </div>
        </div>
      )}

      {audioKey && (
        <div className={Styles.demoSection}>
          <Label text={Localized.AboutDemoListenLabel} classNameContainer={Styles.sectionLabel} />
          <Label text={Localized.AboutDemoListenTitle} classNameContainer={Styles.sectionTitle} />
          <div className={Styles.bubblePanel}>
            <Audio
              audioUrl={audioUrl}
              audioFallback={Localized.AboutDemoAudioFallback}
              loadingText={Localized.DashLoading}
              rtl={isRTL}
              classNameContainer={Styles.audioWrap}
              classNameAudio={Styles.audio}
              classNameLoading={Styles.bubblePanelText}
              telemetryKey={localConstants.telemetrySurfaceDemo}
            />
            <Label text={Localized.AboutDemoListenSubtitle} classNameContainer={Styles.bubblePanelText} />
          </div>
        </div>
      )}

      {videoKey && (
        <div className={Styles.demoSection}>
          <Label text={Localized.AboutDemoWatchLabel} classNameContainer={Styles.sectionLabel} />
          <Label text={Localized.AboutDemoTitle} classNameContainer={Styles.sectionTitle} />
          <div className={Styles.bubblePanel}>
            <Video
              videoUrl={videoUrl}
              videoFallback={Localized.AboutDemoVideoFallback}
              loadingText={Localized.DashLoading}
              rtl={isRTL}
              classNameContainer={Styles.videoWrap}
              classNameVideo={Styles.video}
              classNameLoading={Styles.bubblePanelText}
              telemetryKey={localConstants.telemetrySurfaceDemo}
            />
          </div>
        </div>
      )}
    </section>
  );
}
